// 3rd party imports
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useStores } from 'hooks';
import { observer } from 'mobx-react';

// shared-ui
import { UitkButtonSize, UitkSecondaryButton } from 'uitk-react-button';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkLoader } from 'uitk-react-loader';
import { useLocalization } from '@shared-ui/localization-context';

import { Layer } from 'bernie-view';
import { UitkDialog, UitkDialogContent } from 'uitk-react-dialog';
import { DialogRoute, closeDialog, updateSearch } from 'bernie-core';
import { useLogger } from '@shared-ui/logger-context';
import { OauthEvents, OAUTH_TAG } from '../../observability/events/oauth';
import { PzPublisher, PzUser } from '../../models/pz';

interface PzOauthFormProps {
  email: string;
  handleOauth: (partnerId: string, pzAccount: string, pzRefreshToken: string) => void;
}

export const PzOauthForm: React.FunctionComponent<PzOauthFormProps> = observer(({ email, handleOauth }) => {
  const history = useHistory();
  const location = useLocation();
  const l10n = useLocalization();
  const { pzOAuthStore, pzStore, uiStore } = useStores();

  const LOGGER = useLogger();
  LOGGER.info(`${OAUTH_TAG}.components`);

  const [pzFormLoaded, setPzFormLoaded] = React.useState(false);
  const [authToken, setAuthToken] = React.useState('');

  const onMessageHandler = (event) => {
    let data;
    if (typeof event.data === 'string' || event.data instanceof String) {
      data = JSON.parse(event.data);
      setAuthToken(data.code);
    }
  };

  const handleIframeLoad = () => {
    setPzFormLoaded(true);
  };

  window.addEventListener('message', onMessageHandler);

  const pzOauthUrl = `https://api.partnerize.com/oauth/auth?response_type=code&client_id=${uiStore.pzClientId}&redirect_uri=${uiStore.pzRedirectUri}`;

  const handleClick = () => {
    updateSearch({ history, location, newParams: { pwaDialog: 'oauthFrom' } });
  };

  const onDismiss = () => {
    closeDialog({ history, location, key: 'oauthFrom' });
  };

  const getPzDetails = async (token, refreshToken) => {
    try {
      const partner: PzPublisher = await pzStore.fetchPartner(token);

      const partnerId = partner.publisher_id;
      const accountName = partner.account_name;
      handleOauth(partnerId, accountName, refreshToken);

      closeDialog({ history, location, key: 'oauthFrom' });
    } catch (err) {
      LOGGER.error(OauthEvents.FAILED, JSON.stringify(err));
    }
  };

  React.useEffect(() => {
    if (authToken.length) {
      pzOAuthStore.fetchAccessToken(authToken, email).then(({ accessToken, refreshToken }) => {
        setPzFormLoaded(false);
        getPzDetails(accessToken, refreshToken);
      });
    }
  }, [authToken]);

  return (
    <div className="PzOauthForm">
      <DialogRoute id="oauthFrom">
        <Layer id="dialog" onDismiss={onDismiss}>
          <UitkDialog returnFocusOnClose={true}>
            <UitkDialogContent key="dialogContent1" className="PzOauthForm__dialog">
              {!pzFormLoaded && <UitkLoader className="PzOauthForm__loader" />}
              <iframe src={pzOauthUrl} onLoad={handleIframeLoad} />
            </UitkDialogContent>
          </UitkDialog>
        </Layer>
      </DialogRoute>

      <UitkSpacing margin={{ blockend: 'six' }}>
        <UitkSecondaryButton
          disabled={!email}
          size={UitkButtonSize.LARGE}
          className="SignupForm__pz"
          onClick={handleClick}
        >
          <>
            <i className="SignupForm__icon" />
            {l10n.formatText('pz.signup.button.link_your_partnerize_account')}
          </>
        </UitkSecondaryButton>
      </UitkSpacing>
    </div>
  );
});
